import {
  Box,
  HStack,
  IconButton,
  Portal,
  Separator,
  VStack
} from "@chakra-ui/react"
import { ExpandIcon } from "@pathwright/pathicons"
import useHoverCardOrPopover from "@pathwright/ux/src/components/chakra/useHoverCardOrPopover"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { ReactNode, useEffect, useState } from "react"
import { PathNavigatorIndex } from "./PathNavigator"
import { usePathNavigator } from "./PathNavigatorContext"
import PathNavigatorSearch from "./PathNavigatorSearch"

type InlinePathNavigatorProps = {
  renderTrigger: (args: {
    onOpen: () => void
    onClose: () => void
  }) => ReactNode
  groupId?: number
  startExpanded?: boolean
  displayMainNavigatorButton?: boolean
}

const InlinePathNavigatorContent = ({
  renderTrigger,
  groupId,
  startExpanded = false,
  displayMainNavigatorButton = true,
}: InlinePathNavigatorProps) => {
  const { hasHover, isOpen, onOpen, onClose, inlineMode, setIsInlineMode } =
    usePathNavigator()

  // Initially (until Search is focused), the Inline Navigator shows only Lesson and Steps for the current Step/Group
  const [showCurrentGroupOnly, setShowCurrentGroupOnly] = useState(true)
  useEffect(() => {
    if (!isOpen) {
      setShowCurrentGroupOnly(true)
    }
  }, [isOpen])

  const handleOpen = () => {
    onOpen()
    setIsInlineMode(true)
  }

  const handleClose = () => {
    // this early return prevents closing the Main Navigator
    // when the Inline Navigator is being closed at the same time
    // since both rely on the same isOpen state
    if (!inlineMode) return

    onClose(false) // don't clear search
    setShowCurrentGroupOnly(true)
  }

  let HoverCardOrPopover = useHoverCardOrPopover()
  // HoverCardOrPopover = Popover

  return (
    <HoverCardOrPopover.Root
      open={isOpen && inlineMode}
      onOpenChange={(e: { open: boolean }) =>
        e.open ? handleOpen() : handleClose()
      }
      // If hover isn't supported, autoFocus the HoverCardOrPopover
      // this allows blur on close to work without having to first click/touch inside the HoverCardOrPopover
      autoFocus={!hasHover}
      lazyMount
      unmountOnExit
    >
      <HoverCardOrPopover.Trigger>
        {renderTrigger({
          onOpen: handleOpen,
          onClose: handleClose
        })}
      </HoverCardOrPopover.Trigger>
      <Portal>
        <HoverCardOrPopover.Positioner>
          <HoverCardOrPopover.Content
            maxW="24rem"
            minWidth="max(24rem, 100%)"
            className="path-navigator-inline-mode"
            borderWidth="0px"
            borderRadius="card"
            backdropFilter="blur(5px)"
            pt={2}
            pb={4}
            px={4}
            css={{
              "--popover-bg": "{colors.blackAlpha.900}",
              "--hovercard-bg": "{colors.blackAlpha.900}"
            }}
          >
            <HoverCardOrPopover.Arrow>
              <HoverCardOrPopover.ArrowTip
                borderWidth="0"
                borderColor="transparent"
                shadow="md"
              />
            </HoverCardOrPopover.Arrow>
            <VStack
              align="stretch"
              separator={
                <Separator
                  borderBottomWidth={0}
                  borderTopWidth="1px"
                  w="full"
                />
              }
            >
              <Box>
                {displayMainNavigatorButton && (
                  <HStack justify="end" w="100%">
                    <IconButton
                      onClick={() => setIsInlineMode(false)}
                      aria-label="Open Main Path Navigator"
                      colorPalette="whiteAlpha"
                      variant="ghost"
                      size="md"
                      rounded="full"
                      children={<Pathicon icon="sidebar-side" />}
                    />
                  </HStack>
                )}
                <PathNavigatorSearch
                  onFocus={() => setShowCurrentGroupOnly(false)}
                />
              </Box>
              <Box maxH="calc(100vh - 180px)" overflowY="auto">
                <PathNavigatorIndex
                  inlineGroupId={showCurrentGroupOnly ? groupId : undefined}
                />
                {showCurrentGroupOnly && !startExpanded ? (
                  <HStack w="100%" justify="center" pb={2} pt={8}>
                    <IconButton
                      // NOTE: when clicking this results in the content height
                      // not expanding to reach the point at which the user clicked,
                      // the menu will close. Probably related to some click outside behavior,
                      // which doesn't seem to be mitigated by a onMouseUp handler.
                      onClick={() => setShowCurrentGroupOnly(false)}
                      aria-label="Expand Path Navigator"
                      colorPalette="whiteAlpha"
                      variant="ghost"
                      size="md"
                      rounded="full"
                      children={<ExpandIcon name="expand" />}
                    />
                  </HStack>
                ) : null}
              </Box>
            </VStack>
          </HoverCardOrPopover.Content>
        </HoverCardOrPopover.Positioner>
      </Portal>
    </HoverCardOrPopover.Root>
  )
}

const InlinePathNavigator = (props: InlinePathNavigatorProps) => {
  const pathNavigator = usePathNavigator()

  // The Step view can outlive ResourcePath briefly during legacy route changes.
  // Navigation is optional in that transition, so wait for its provider instead
  // of crashing the route that opened the export panel.
  return pathNavigator ? <InlinePathNavigatorContent {...props} /> : null
}

export default InlinePathNavigator
